/**
 * DaypartLegend Component
 * Displays the Daypart-specific metrics guide for Stream Monitor
 * 
 * SCOPE: ONLY for Daypart view in Songs Analyzer
 * NOT for: Charts, Full Day views, Total views
 * 
 * Shows listener BEHAVIOR metrics, NOT chart ranking
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info, Clock, Users, TrendingUp, Activity } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const DaypartLegend = ({ currentDaypart }) => {
  const { t } = useLanguage();
  const [showGuide, setShowGuide] = useState(false);
  
  return (
    <>
      {/* Daypart Metrics Header */}
      <div className="text-sm font-medium flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 py-3 px-4 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 rounded-lg border border-indigo-500/30">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-400" />
          <span className="font-bold text-indigo-300">{t('daypartMetrics')}</span>
          <span className="text-xs text-indigo-400/70">– {t('daypartSubtitle')}</span>
        </div>
        
        {/* Quick metric badges */}
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="flex items-center gap-1 px-2 py-1 bg-cyan-500/20 rounded text-cyan-300" title="Daypart Retention">
            <Users className="w-3 h-3" />
            DP-Ret%
          </span>
          <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300" title="Relative Engagement Index">
            <TrendingUp className="w-3 h-3" />
            REI
          </span>
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 rounded text-amber-300" title="Daypart Early Reaction">
            <Activity className="w-3 h-3" />
            DP-Early
          </span>
        </div>
        
        {/* Guide toggle button */}
        <button 
          onClick={() => setShowGuide(!showGuide)}
          className="ml-auto flex items-center gap-1 text-indigo-400 hover:text-indigo-300 transition-colors"
          data-testid="daypart-guide-toggle"
        >
          <Info className="w-4 h-4" />
          <span>{t('daypartGuide')}</span>
          {showGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>
      
      {/* Detailed Guide - Expandable */}
      {showGuide && (
        <div className="mb-4 p-4 bg-indigo-900/20 rounded-lg border border-indigo-500/30 text-sm">
          {/* Main Explanation */}
          <div className="mb-4 p-3 bg-indigo-800/30 rounded border border-indigo-600/30">
            <h4 className="font-bold text-indigo-300 mb-2 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Daypart Metrics – Αντίδραση Ακροατών ανά Ζώνη Ώρας
            </h4>
            <p className="text-indigo-200/80 text-xs leading-relaxed">
              Τα παρακάτω metrics δείχνουν πώς αντέδρασαν οι ακροατές στο συγκεκριμένο τραγούδι 
              μέσα σε αυτή τη χρονική ζώνη, σε σύγκριση με άλλα τραγούδια που έπαιξαν στο ίδιο daypart.
              <br />
              <strong className="text-amber-300">Δεν αποτελούν chart ranking</strong>, αλλά ένδειξη συμπεριφοράς ακροατών ανά ώρα μετάδοσης.
            </p>
          </div>
          
          {/* Metrics Definitions */}
          <div className="mb-4">
            <h5 className="font-semibold text-indigo-300 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Επεξήγηση Μετρήσεων Daypart:
            </h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-indigo-900/50">
                    <th className="border border-indigo-600/50 px-3 py-2 text-left text-indigo-200">Μετρική</th>
                    <th className="border border-indigo-600/50 px-3 py-2 text-left text-indigo-200">Τι Μετράει</th>
                    <th className="border border-indigo-600/50 px-3 py-2 text-center text-indigo-200">Καλό</th>
                    <th className="border border-indigo-600/50 px-3 py-2 text-center text-indigo-200">Μέτριο</th>
                  </tr>
                </thead>
                <tbody className="text-indigo-100/80">
                  <tr className="hover:bg-indigo-800/20">
                    <td className="border border-indigo-600/50 px-3 py-2">
                      <span className="font-bold text-cyan-400">DP-Ret%</span>
                      <br />
                      <span className="text-[10px] text-indigo-400">Daypart Retention</span>
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2">
                      Πόσο καλά κράτησε τους ακροατές το τραγούδι σε αυτή τη ζώνη ώρας, 
                      σε σχέση με τη συνολική ακρόαση του daypart
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-green-400">&gt;5%</td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-yellow-400">1-5%</td>
                  </tr>
                  <tr className="hover:bg-indigo-800/20">
                    <td className="border border-indigo-600/50 px-3 py-2">
                      <span className="font-bold text-emerald-400">REI</span>
                      <br />
                      <span className="text-[10px] text-indigo-400">Relative Engagement Index</span>
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2">
                      Συγκρίνει την απόδοση του τραγουδιού με τον μέσο όρο όλων των τραγουδιών 
                      της συγκεκριμένης ζώνης ώρας
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-green-400">&gt;1.2</td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-yellow-400">0.8-1.2</td>
                  </tr>
                  <tr className="hover:bg-indigo-800/20">
                    <td className="border border-indigo-600/50 px-3 py-2">
                      <span className="font-bold text-amber-400">DP-Early</span>
                      <br />
                      <span className="text-[10px] text-indigo-400">Daypart Early Reaction</span>
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2">
                      Δείχνει αν το τραγούδι προκαλεί γρήγορη αποχώρηση ακροατών στα πρώτα δευτερόλεπτα, 
                      σε σύγκριση με άλλα τραγούδια του ίδιου daypart
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-green-400">&gt;70</td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-yellow-400">40-70</td>
                  </tr>
                  <tr className="hover:bg-indigo-800/20">
                    <td className="border border-indigo-600/50 px-3 py-2">
                      <span className="font-bold text-purple-400">DP-Score</span>
                      <br />
                      <span className="text-[10px] text-indigo-400">Daypart Composite Score</span>
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2">
                      Συνολική εικόνα απόδοσης του τραγουδιού στο συγκεκριμένο daypart 
                      (0-100, δεν χρησιμοποιείται για charts)
                    </td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-green-400">&gt;70</td>
                    <td className="border border-indigo-600/50 px-3 py-2 text-center text-yellow-400">40-70</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Formula Explanation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-cyan-900/20 rounded border border-cyan-600/30">
              <h6 className="font-semibold text-cyan-300 mb-2">📊 Τύπος DP-Ret%</h6>
              <code className="block text-[10px] bg-black/30 p-2 rounded text-cyan-200">
                DP-Ret% = TLH τραγουδιού στο daypart / Συνολικό TLH του σταθμού στο ίδιο daypart
              </code>
            </div>
            <div className="p-3 bg-emerald-900/20 rounded border border-emerald-600/30">
              <h6 className="font-semibold text-emerald-300 mb-2">📈 Τύπος REI</h6>
              <code className="block text-[10px] bg-black/30 p-2 rounded text-emerald-200">
                REI = (Μέσο TLH/play τραγουδιού) / (Μέσο TLH/play όλων τραγουδιών daypart)
              </code>
            </div>
          </div>
          
          {/* What's NOT shown in Dayparts */}
          <div className="p-3 bg-red-900/20 rounded border border-red-600/30">
            <h5 className="font-semibold text-red-300 mb-2">⛔ Τι ΔΕΝ εμφανίζεται στα Dayparts:</h5>
            <div className="flex flex-wrap gap-3 text-xs text-red-200/80">
              <span className="px-2 py-1 bg-red-800/30 rounded">❌ Chart Rank</span>
              <span className="px-2 py-1 bg-red-800/30 rounded">❌ People&apos;s Chart Score</span>
              <span className="px-2 py-1 bg-red-800/30 rounded">❌ POWER / RECURRENT / GOLD</span>
            </div>
            <p className="mt-2 text-[10px] text-red-200/60 italic">
              Αυτά αφορούν ολόκληρο χρονικό εύρος, όχι επιμέρους time slots.
            </p>
          </div>
          
          {/* Final Principle */}
          <div className="mt-4 p-3 bg-gradient-to-r from-indigo-800/30 to-purple-800/30 rounded border border-indigo-500/30">
            <p className="text-center text-indigo-200 text-xs font-medium">
              🔐 <strong>Τα charts δείχνουν θέση. Τα dayparts δείχνουν συμπεριφορά.</strong>
              <br />
              <span className="text-indigo-300/70">Και τα δύο είναι απαραίτητα — αλλά δεν συγχέονται.</span>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default DaypartLegend;
